import React, { useState, useEffect } from 'react';
import logo from '../assets/logo.png';
import { Link, useSearchParams } from 'react-router-dom';
import { 
  Search, 
  Grid3x3, 
  List,
  ChevronLeft,
  ChevronRight,
  FileText,
  BookOpen,
  Zap,
  FileCheck,
  FolderOpen,
  Eye,
  Calendar,
  GraduationCap,
  Archive,
  Filter,
  X,
  Layers
} from 'lucide-react';

function BrowsePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [allResources, setAllResources] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [degrees, setDegrees] = useState([]);
  const [levels, setLevels] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filteredResources, setFilteredResources] = useState([]);
  
  const [viewMode, setViewMode] = useState('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const itemsPerPage = 12;
  
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProgram, setSelectedProgram] = useState(searchParams.get('program') || 'all');
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [selectedLevel, setSelectedLevel] = useState('all');
  const [selectedSubject, setSelectedSubject] = useState('all');
  const [selectedSemester, setSelectedSemester] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  
  const [availableDegrees, setAvailableDegrees] = useState([]);
  const [availableLevels, setAvailableLevels] = useState([]);
  const [availableSubjects, setAvailableSubjects] = useState([]);
  const [availableSemesters, setAvailableSemesters] = useState([]);

  // Fetch all data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        const [resourcesRes, programsRes, degreesRes, levelsRes, subjectsRes] = await Promise.all([
          fetch('https://unwavering-luck-2076e754ea.strapiapp.com/api/resources?populate[subject][populate][level][populate][degree][populate]=program'),
          fetch('https://unwavering-luck-2076e754ea.strapiapp.com/api/programs'),
          fetch('https://unwavering-luck-2076e754ea.strapiapp.com/api/degrees'),
          fetch('https://unwavering-luck-2076e754ea.strapiapp.com/api/levels'),
          fetch('https://unwavering-luck-2076e754ea.strapiapp.com/api/subjects?populate[level][populate][degree][populate]=program')
        ]);
        
        const resourcesData = await resourcesRes.json();
        const programsData = await programsRes.json();
        const degreesData = await degreesRes.json();
        const levelsData = await levelsRes.json();
        const subjectsData = await subjectsRes.json();
        
        setPrograms(programsData.data || []);
        setDegrees(degreesData.data || []);
        setLevels(levelsData.data || []);
        setSubjects(subjectsData.data || []);
        
        const allResourcesData = resourcesData.data || [];
        const approvedResources = allResourcesData.filter(r => r.approvalStatus === 'approved');
        
        // Transform resources with documentId included
        const transformed = approvedResources.map(r => {
          const subject = r.subject;
          const level = subject?.level;
          const degree = level?.degree;
          const program = degree?.program;
          
          return {
            id: r.id,
            documentId: r.documentId,
            title: r.title,
            type: r.type,
            createdAt: r.createdAt,
            file: r.file,
            subjectId: subject?.id,
            subjectName: subject?.name,
            subjectSemester: subject?.semester,
            levelId: level?.id,
            levelName: level?.name,
            degreeId: degree?.id,
            degreeName: degree?.name,
            programId: program?.id,
            programName: program?.name
          };
        });
        
        setAllResources(transformed);
        
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Update available degrees based on selected program
  useEffect(() => {
    if (selectedProgram === 'all') {
      setAvailableDegrees(degrees);
    } else {
      const filtered = degrees.filter(d => d.program?.id === parseInt(selectedProgram));
      setAvailableDegrees(filtered);
    }
    setSelectedDepartment('all');
    setSelectedLevel('all');
    setSelectedSubject('all');
    setSelectedSemester('all');
  }, [selectedProgram, degrees]);

  // Update available levels based on selected department
  useEffect(() => {
    if (selectedDepartment === 'all') {
      if (selectedProgram === 'all') {
        setAvailableLevels(levels);
      } else {
        const programDegreeIds = availableDegrees.map(d => d.id);
        const filtered = levels.filter(l => programDegreeIds.includes(l.degree?.id));
        setAvailableLevels(filtered);
      }
    } else {
      const filtered = levels.filter(l => l.degree?.id === parseInt(selectedDepartment));
      setAvailableLevels(filtered);
    }
    setSelectedLevel('all');
    setSelectedSubject('all');
    setSelectedSemester('all');
  }, [selectedDepartment, selectedProgram, availableDegrees, levels]);

  // Update available subjects based on selected level
  useEffect(() => {
    if (selectedLevel !== 'all') {
      const filtered = subjects.filter(s => s.level?.id === parseInt(selectedLevel));
      setAvailableSubjects(filtered);
    } else if (selectedDepartment !== 'all') {
      const departmentLevelIds = availableLevels.map(l => l.id);
      const filtered = subjects.filter(s => departmentLevelIds.includes(s.level?.id));
      setAvailableSubjects(filtered);
    } else if (selectedProgram !== 'all') {
      const programLevelIds = availableLevels.map(l => l.id);
      const filtered = subjects.filter(s => programLevelIds.includes(s.level?.id));
      setAvailableSubjects(filtered);
    } else {
      setAvailableSubjects(subjects);
    }
    setSelectedSubject('all');
    setSelectedSemester('all');
  }, [selectedLevel, selectedDepartment, selectedProgram, availableLevels, subjects]);

  // Update available semesters based on selected subject
  useEffect(() => {
    if (selectedSubject !== 'all') {
      const subject = subjects.find(s => s.id === parseInt(selectedSubject));
      if (subject && subject.semester) {
        setAvailableSemesters([subject.semester]);
      } else {
        setAvailableSemesters([]);
      }
    } else {
      const semesters = [...new Set(availableSubjects.map(s => s.semester).filter(Boolean))];
      setAvailableSemesters(semesters);
    }
    setSelectedSemester('all');
  }, [selectedSubject, availableSubjects, subjects]);

  // Apply all filters
  useEffect(() => {
    let filtered = [...allResources];
    
    if (searchTerm) {
      const query = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(r => 
        r.title?.toLowerCase().includes(query) ||
        r.type?.toLowerCase().includes(query) ||
        r.subjectName?.toLowerCase().includes(query)
      );
    }
    
    if (selectedType !== 'all') {
      filtered = filtered.filter(r => r.type?.toUpperCase() === selectedType.toUpperCase());
    }
    
    if (selectedProgram !== 'all') {
      filtered = filtered.filter(r => r.programId === parseInt(selectedProgram));
    }
    
    if (selectedDepartment !== 'all') {
      filtered = filtered.filter(r => r.degreeId === parseInt(selectedDepartment));
    }
    
    if (selectedLevel !== 'all') {
      filtered = filtered.filter(r => r.levelId === parseInt(selectedLevel));
    }
    
    if (selectedSubject !== 'all') {
      filtered = filtered.filter(r => r.subjectId === parseInt(selectedSubject));
    }
    
    if (selectedSemester !== 'all') {
      filtered = filtered.filter(r => r.subjectSemester === selectedSemester);
    }
    
    setFilteredResources(filtered);
    setCurrentPage(1);
    
  }, [searchTerm, selectedType, selectedProgram, selectedDepartment, selectedLevel, selectedSubject, selectedSemester, allResources]);

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedProgram('all');
    setSelectedDepartment('all');
    setSelectedLevel('all');
    setSelectedSubject('all');
    setSelectedSemester('all');
    setSelectedType('all');
    setSearchParams({});
  };

  const totalPages = Math.ceil(filteredResources.length / itemsPerPage);
  const paginatedResources = filteredResources.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const resourceTypes = [
    { id: 'all', name: 'Tous', icon: <Layers size={14} /> },
    { id: 'cours', name: 'Cours', icon: <BookOpen size={14} /> },
    { id: 'examens', name: 'Examens', icon: <FileText size={14} /> },
    { id: 'tp', name: 'TP', icon: <Zap size={14} /> },
    { id: 'td', name: 'TD', icon: <FileCheck size={14} /> },
    { id: 'projets', name: 'Projets', icon: <FolderOpen size={14} /> },
  ];

  const getTypeIcon = (type) => {
    switch(type?.toUpperCase()) {
      case 'COURS': return <BookOpen size={18} className="text-mint" />;
      case 'EXAMENS': return <FileText size={18} className="text-pink" />;
      case 'TP': return <Zap size={18} className="text-yellow" />;
      case 'TD': return <FileCheck size={18} className="text-orange" />;
      case 'PROJETS': return <FolderOpen size={18} className="text-mint" />;
      default: return <FileText size={18} className="text-white/40" />;
    }
  };

  const getTypeColor = (type) => {
    switch(type?.toUpperCase()) {
      case 'COURS': return 'border-mint/30 text-mint';
      case 'EXAMENS': return 'border-pink/30 text-pink';
      case 'TP': return 'border-yellow/30 text-yellow';
      case 'TD': return 'border-orange/30 text-orange';
      case 'PROJETS': return 'border-mint/30 text-mint';
      default: return 'border-white/20 text-white/40';
    }
  };

  const getSubjectName = (resource) => {
    return resource.subjectName || 'Général';
  };

  const getLevelName = (resource) => {
    return resource.levelName || 'N/A';
  };

  const getDepartmentName = (resource) => {
    return resource.degreeName || 'ISIMM';
  };

  const activeFilterCount = [
    selectedType !== 'all',
    selectedProgram !== 'all',
    selectedDepartment !== 'all',
    selectedLevel !== 'all',
    selectedSubject !== 'all',
    selectedSemester !== 'all',
    searchTerm !== ''
  ].filter(Boolean).length;

  const getSelectedProgramName = () => {
    const program = programs.find(p => p.id === parseInt(selectedProgram));
    return program?.name || 'Tous les programmes';
  };

  const getSelectedDepartmentName = () => {
    const degree = degrees.find(d => d.id === parseInt(selectedDepartment));
    return degree?.name || 'Tous les départements';
  };

  const getSelectedLevelName = () => {
    const level = levels.find(l => l.id === parseInt(selectedLevel));
    return level?.name || 'Tous les niveaux';
  };

  const getSelectedSubjectName = () => {
    const subject = subjects.find(s => s.id === parseInt(selectedSubject));
    return subject?.name || 'Toutes les matières';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-dark-bg flex items-center justify-center">
        <div className="text-white/40">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="bg-dark-bg min-h-screen">
      
      <div className="border-b border-white/10 bg-dark-surface/30 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link to="/" className="flex items-center gap-3 sm:gap-4">
  <img 
    src={logo} 
    alt="ISIMM Archive" 
    className="w-10 h-10 sm:w-12 sm:h-12 object-contain"
    style={{ scale: '1.3' }}
  />
  <div>
    <span className="text-lg sm:text-xl font-mono tracking-tighter text-white">ISIMM<span className="gradient-text">_Archive</span></span>
    <p className="font-mono text-[7px] sm:text-[9px] tracking-wider text-white/40 hidden sm:block">digital library</p>
  </div>
</Link>
              <span className="text-white/20">/</span>
              <span className="font-mono text-sm text-white/60">Bibliothèque</span>
            </div>
            <Link to="/" className="font-mono text-xs text-white/40 hover:text-mint transition flex items-center gap-1"><ChevronLeft size={12} /> Accueil</Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        
        {/* Active filters bar */}
        {activeFilterCount > 0 && (
          <div className="mb-6 flex flex-wrap gap-2">
            {selectedProgram !== 'all' && (
              <span className="px-3 py-1.5 bg-mint/10 border border-mint/30 rounded-full text-mint text-xs font-mono flex items-center gap-1">
                Programme: {getSelectedProgramName()}
                <button onClick={() => setSelectedProgram('all')} className="ml-1 hover:text-pink"><X size={12} /></button>
              </span>
            )}
            {selectedDepartment !== 'all' && (
              <span className="px-3 py-1.5 bg-pink/10 border border-pink/30 rounded-full text-pink text-xs font-mono flex items-center gap-1">
                Département: {getSelectedDepartmentName()}
                <button onClick={() => setSelectedDepartment('all')} className="ml-1 hover:text-mint"><X size={12} /></button>
              </span>
            )}
            {selectedLevel !== 'all' && (
              <span className="px-3 py-1.5 bg-yellow/10 border border-yellow/30 rounded-full text-yellow text-xs font-mono flex items-center gap-1">
                Niveau: {getSelectedLevelName()}
                <button onClick={() => setSelectedLevel('all')} className="ml-1 hover:text-pink"><X size={12} /></button>
              </span>
            )}
            {selectedSubject !== 'all' && (
              <span className="px-3 py-1.5 bg-orange/10 border border-orange/30 rounded-full text-orange text-xs font-mono flex items-center gap-1">
                Matière: {getSelectedSubjectName()}
                <button onClick={() => setSelectedSubject('all')} className="ml-1 hover:text-mint"><X size={12} /></button>
              </span>
            )}
            {selectedSemester !== 'all' && (
              <span className="px-3 py-1.5 bg-mint/10 border border-mint/30 rounded-full text-mint text-xs font-mono flex items-center gap-1">
                Semestre: {selectedSemester}
                <button onClick={() => setSelectedSemester('all')} className="ml-1 hover:text-pink"><X size={12} /></button>
              </span>
            )}
            {selectedType !== 'all' && (
              <span className="px-3 py-1.5 bg-pink/10 border border-pink/30 rounded-full text-pink text-xs font-mono flex items-center gap-1">
                Type: {selectedType.toUpperCase()}
                <button onClick={() => setSelectedType('all')} className="ml-1 hover:text-mint"><X size={12} /></button>
              </span>
            )}
            {searchTerm && (
              <span className="px-3 py-1.5 bg-white/10 border border-white/20 rounded-full text-white/60 text-xs font-mono flex items-center gap-1">
                Recherche: "{searchTerm}"
                <button onClick={() => setSearchTerm('')} className="ml-1 hover:text-pink"><X size={12} /></button>
              </span>
            )}
            <button onClick={clearFilters} className="px-3 py-1.5 bg-dark-surface border border-white/10 rounded-full text-white/40 text-xs font-mono hover:text-mint transition">Effacer tout</button>
          </div>
        )}
        
        {/* Search and filter toggle */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
            <input 
              type="text" 
              placeholder="Rechercher par titre, matière..." 
              value={searchTerm} 
              onChange={(e) => setSearchTerm(e.target.value)} 
              className="w-full pl-11 pr-4 py-3 bg-dark-surface border border-white/10 rounded-xl text-white placeholder:text-white/30 focus:outline-none focus:border-mint focus:ring-1 focus:ring-mint transition" 
            />
            {searchTerm && <button onClick={() => setSearchTerm('')} className="absolute right-4 top-1/2 -translate-y-1/2 text-white/30 hover:text-white"><X size={16} /></button>}
          </div>
          
          <div className="flex gap-3">
            <button 
              onClick={() => setShowFilters(!showFilters)} 
              className={`px-4 py-3 rounded-xl transition flex items-center gap-2 ${showFilters ? 'bg-mint text-dark-bg' : 'bg-dark-surface border border-white/10 text-white/60 hover:text-white'}`}
            >
              <Filter size={16} /><span className="font-mono text-xs">Filtres</span>
              {activeFilterCount > 0 && <span className="ml-1 bg-dark-bg text-mint text-[10px] px-1.5 py-0.5 rounded-full">{activeFilterCount}</span>}
            </button>
            <div className="flex gap-2 bg-dark-surface rounded-xl border border-white/10 p-1">
              <button onClick={() => setViewMode('grid')} className={`p-2 rounded-lg transition ${viewMode === 'grid' ? 'bg-mint text-dark-bg' : 'text-white/40 hover:text-white'}`}><Grid3x3 size={18} /></button>
              <button onClick={() => setViewMode('list')} className={`p-2 rounded-lg transition ${viewMode === 'list' ? 'bg-mint text-dark-bg' : 'text-white/40 hover:text-white'}`}><List size={18} /></button>
            </div>
          </div>
        </div>
        
        {/* Filter panel */}
        {showFilters && (
          <div className="bg-dark-surface/50 border border-white/10 rounded-xl p-5 mb-6 animate-fade-up">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-white/50 text-xs font-mono mb-2">PROGRAMME</label>
                <select 
                  value={selectedProgram} 
                  onChange={(e) => setSelectedProgram(e.target.value)} 
                  className="w-full px-3 py-2 bg-dark-card border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-mint focus:ring-1 focus:ring-mint transition"
                >
                  <option value="all">Tous les programmes</option>
                  {programs.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              
              <div>
                <label className="block text-white/50 text-xs font-mono mb-2">DÉPARTEMENT</label>
                <select 
                  value={selectedDepartment} 
                  onChange={(e) => setSelectedDepartment(e.target.value)} 
                  className="w-full px-3 py-2 bg-dark-card border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-mint focus:ring-1 focus:ring-mint transition disabled:opacity-50" 
                  disabled={availableDegrees.length === 0}
                >
                  <option value="all">Tous les départements</option>
                  {availableDegrees.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>
              
              <div>
                <label className="block text-white/50 text-xs font-mono mb-2">NIVEAU</label>
                <select 
                  value={selectedLevel} 
                  onChange={(e) => setSelectedLevel(e.target.value)} 
                  className="w-full px-3 py-2 bg-dark-card border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-mint focus:ring-1 focus:ring-mint transition disabled:opacity-50" 
                  disabled={availableLevels.length === 0}
                >
                  <option value="all">Tous les niveaux</option>
                  {availableLevels.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </div>
              
              <div>
                <label className="block text-white/50 text-xs font-mono mb-2">MATIÈRE</label>
                <select 
                  value={selectedSubject} 
                  onChange={(e) => setSelectedSubject(e.target.value)} 
                  className="w-full px-3 py-2 bg-dark-card border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-mint focus:ring-1 focus:ring-mint transition disabled:opacity-50" 
                  disabled={availableSubjects.length === 0}
                >
                  <option value="all">Toutes les matières</option>
                  {availableSubjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              
              <div>
                <label className="block text-white/50 text-xs font-mono mb-2">SEMESTRE</label>
                <select 
                  value={selectedSemester} 
                  onChange={(e) => setSelectedSemester(e.target.value)} 
                  className="w-full px-3 py-2 bg-dark-card border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-mint focus:ring-1 focus:ring-mint transition disabled:opacity-50" 
                  disabled={availableSemesters.length === 0}
                >
                  <option value="all">Tous les semestres</option>
                  {availableSemesters.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              
              <div>
                <label className="block text-white/50 text-xs font-mono mb-2">TYPE</label>
                <select 
                  value={selectedType} 
                  onChange={(e) => setSelectedType(e.target.value)} 
                  className="w-full px-3 py-2 bg-dark-card border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-mint focus:ring-1 focus:ring-mint transition"
                >
                  {resourceTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
            </div>
          </div>
        )}
        
        {/* Results count */}
        <div className="flex justify-between items-center mb-6">
          <div className="text-white/40 text-sm">
            {filteredResources.length} document{filteredResources.length !== 1 ? 's' : ''} trouvé{filteredResources.length !== 1 ? 's' : ''}
          </div>
        </div>

        {/* Results - Grid View with documentId links */}
        {filteredResources.length === 0 ? (
          <div className="text-center py-20">
            <div className="text-white/40 text-lg">Aucun document trouvé</div>
            <div className="text-white/20 text-sm mt-2">Essayez d'autres filtres</div>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {paginatedResources.map((resource) => (
              <Link key={resource.id} to={`/resource/${resource.documentId}`} className="bg-dark-surface/50 border border-white/10 rounded-xl p-5 hover:border-mint/30 hover:-translate-y-1 transition-all duration-200 group">
                <div className="flex items-start justify-between mb-3">
                  <div className="w-12 h-12 rounded-xl bg-mint/10 flex items-center justify-center group-hover:bg-mint/20 transition">{getTypeIcon(resource.type)}</div>
                  <span className={`font-mono text-[9px] tracking-wider px-2 py-1 rounded border ${getTypeColor(resource.type)}`}>{resource.type}</span>
                </div>
                <h3 className="font-mono text-base text-white group-hover:text-mint transition mb-2 line-clamp-2">{resource.title}</h3>
                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/5">
                  <BookOpen size={12} className="text-mint" />
                  <span className="font-mono text-[10px] text-white/60">{getSubjectName(resource)}</span>
                  <span className="text-white/20">•</span>
                  <GraduationCap size={12} className="text-pink" />
                  <span className="font-mono text-[10px] text-white/40">{getDepartmentName(resource)} {getLevelName(resource)}</span>
                </div>
                <div className="flex items-center gap-3 text-white/30 text-xs mt-3">
                  <span className="flex items-center gap-1"><Calendar size={10} /> {resource.createdAt ? new Date(resource.createdAt).toISOString().split('T')[0] : '2026'}</span>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {paginatedResources.map((resource) => (
              <Link key={resource.id} to={`/resource/${resource.documentId}`} className="bg-dark-surface/50 border border-white/10 rounded-xl p-4 flex items-center justify-between hover:border-mint/30 hover:bg-dark-surface/70 transition-all duration-200 group">
                <div className="flex items-center gap-4 flex-1">
                  <div className="w-10 h-10 rounded-lg bg-mint/10 flex items-center justify-center group-hover:bg-mint/20 transition">{getTypeIcon(resource.type)}</div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-mono text-sm text-white group-hover:text-mint transition">{resource.title}</h3>
                      <span className={`font-mono text-[9px] tracking-wider px-2 py-0.5 rounded border ${getTypeColor(resource.type)}`}>{resource.type}</span>
                    </div>
                    <div className="flex items-center gap-4 mt-2 flex-wrap">
                      <span className="font-mono text-[9px] text-white/30 flex items-center gap-1"><BookOpen size={10} /> {getSubjectName(resource)}</span>
                      <span className="font-mono text-[9px] text-white/30 flex items-center gap-1"><GraduationCap size={10} /> {getDepartmentName(resource)} {getLevelName(resource)}</span>
                      <span className="font-mono text-[9px] text-white/30 flex items-center gap-1"><Calendar size={10} /> {resource.createdAt ? new Date(resource.createdAt).toISOString().split('T')[0] : '2026'}</span>
                    </div>
                  </div>
                </div>
                <Eye size={16} className="text-white/10 group-hover:text-mint transition" />
              </Link>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-8">
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="p-2 rounded-lg bg-dark-surface border border-white/10 text-white/40 hover:text-white disabled:opacity-30 transition"><ChevronLeft size={18} /></button>
            <div className="flex gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) pageNum = i + 1;
                else if (currentPage <= 3) pageNum = i + 1;
                else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                else pageNum = currentPage - 2 + i;
                return (
                  <button key={pageNum} onClick={() => setCurrentPage(pageNum)} className={`w-10 h-10 rounded-lg font-mono text-sm transition ${currentPage === pageNum ? 'bg-mint text-dark-bg' : 'bg-dark-surface border border-white/10 text-white/60 hover:text-white'}`}>
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="p-2 rounded-lg bg-dark-surface border border-white/10 text-white/40 hover:text-white disabled:opacity-30 transition"><ChevronRight size={18} /></button>
          </div>
        )}
      </div>

      <style>{`
        .line-clamp-2 { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fade-up { animation: fadeUp 0.3s ease-out forwards; }
      `}</style>
    </div>
  );
}

export default BrowsePage;